import type { LayoutInput, LayoutResult, GridOptions, Rect } from '../types';
import { GRID_DEFAULTS } from '../defaults';
import { shapeBounds, rectIntersects } from '../utils/geometry';
import { applyAnchor } from '../utils/anchor';

/**
 * Arrange nodes in a grid pattern.
 *
 * When `avoidObstacles` is true and `input.obstacles` is provided, cells that
 * overlap any obstacle (inflated by spacing as a buffer) are skipped. This
 * forces uniform cell sizing — content-mode column widths don't make sense
 * when cells are skipped between columns.
 */
export function grid(
	input: LayoutInput,
	options?: Partial<GridOptions>,
): LayoutResult {
	const opts: GridOptions = { ...GRID_DEFAULTS, ...options };
	const { nodes } = input;

	if (nodes.length === 0) return { nodes: [] };

	// Obstacle-aware path
	if (opts.avoidObstacles) {
		return gridWithObstacles(input, opts);
	}

	return gridStandard(input, opts);
}

/** Original grid algorithm — no obstacle avoidance. */
function gridStandard(input: LayoutInput, opts: GridOptions): LayoutResult {
	const { nodes } = input;
	const allBounds = nodes.map((n) => shapeBounds(n.shape));

	// Determine grid dimensions
	const count = opts.count === 'auto'
		? Math.ceil(Math.sqrt(nodes.length))
		: opts.count;

	const isRow = opts.direction === 'row';
	const cols = isRow ? count : Math.ceil(nodes.length / count);
	const rows = isRow ? Math.ceil(nodes.length / count) : count;

	// Cell sizes
	let cellWidth: number;
	let cellHeight: number;

	if (opts.cellSize === 'uniform') {
		cellWidth = Math.max(...allBounds.map((b) => b.width));
		cellHeight = Math.max(...allBounds.map((b) => b.height));
	} else {
		cellWidth = 0;
		cellHeight = 0;
	}

	const colWidths: number[] = new Array(cols).fill(0);
	const rowHeights: number[] = new Array(rows).fill(0);

	for (let i = 0; i < nodes.length; i++) {
		const col = isRow ? i % cols : Math.floor(i / rows);
		const row = isRow ? Math.floor(i / cols) : i % rows;
		colWidths[col] = Math.max(colWidths[col], allBounds[i].width);
		rowHeights[row] = Math.max(rowHeights[row], allBounds[i].height);
	}

	if (opts.cellSize === 'uniform') {
		colWidths.fill(cellWidth);
		rowHeights.fill(cellHeight);
	}

	const colX: number[] = [0];
	for (let c = 1; c < cols; c++) {
		colX[c] = colX[c - 1] + colWidths[c - 1] + opts.spacing.x;
	}
	const rowY: number[] = [0];
	for (let r = 1; r < rows; r++) {
		rowY[r] = rowY[r - 1] + rowHeights[r - 1] + opts.spacing.y;
	}

	const arranged: { id: string; x: number; y: number }[] = [];

	for (let i = 0; i < nodes.length; i++) {
		const col = isRow ? i % cols : Math.floor(i / rows);
		const row = isRow ? Math.floor(i / cols) : i % rows;
		const b = allBounds[i];

		let alignX = 0;
		if (opts.align.x === 'center') {
			alignX = (colWidths[col] - b.width) / 2;
		} else if (opts.align.x === 'end') {
			alignX = colWidths[col] - b.width;
		}

		let alignY = 0;
		if (opts.align.y === 'center') {
			alignY = (rowHeights[row] - b.height) / 2;
		} else if (opts.align.y === 'end') {
			alignY = rowHeights[row] - b.height;
		}

		arranged.push({
			id: nodes[i].id,
			x: colX[col] + alignX,
			y: rowY[row] + alignY,
		});
	}

	const anchored = applyAnchor(nodes, arranged, opts.anchor);

	return {
		nodes: anchored.map((a) => ({
			id: a.id,
			x: a.x,
			y: a.y,
		})),
	};
}

/** Obstacle-aware grid: skip cells that overlap obstacle rects. */
function gridWithObstacles(input: LayoutInput, opts: GridOptions): LayoutResult {
	const { nodes, obstacles = [] } = input;
	const allBounds = nodes.map((n) => shapeBounds(n.shape));

	// Uniform cell size (content mode doesn't make sense when cells are skipped)
	const cellWidth = Math.max(...allBounds.map((b) => b.width));
	const cellHeight = Math.max(...allBounds.map((b) => b.height));

	const strideX = cellWidth + opts.spacing.x;
	const strideY = cellHeight + opts.spacing.y;

	const cols = opts.count === 'auto'
		? Math.ceil(Math.sqrt(nodes.length))
		: opts.count;

	// Inflate obstacle rects by spacing on all sides as buffer zone
	const inflatedObstacles: Rect[] = obstacles.map((obs) => {
		const b = shapeBounds(obs.shape);
		return {
			x: b.x - opts.spacing.x,
			y: b.y - opts.spacing.y,
			width: b.width + opts.spacing.x * 2,
			height: b.height + opts.spacing.y * 2,
		};
	});

	const originX = opts.origin?.x ?? 0;
	const originY = opts.origin?.y ?? 0;

	const arranged: { id: string; x: number; y: number }[] = [];
	let col = 0;
	let row = 0;
	let cellsScanned = 0;
	const MAX_CELLS = 1000;

	for (let i = 0; i < nodes.length; i++) {
		// Find next free cell
		while (cellsScanned < MAX_CELLS) {
			const cellRect: Rect = {
				x: originX + col * strideX,
				y: originY + row * strideY,
				width: cellWidth,
				height: cellHeight,
			};

			const blocked = inflatedObstacles.some((obs) => rectIntersects(cellRect, obs));
			cellsScanned++;

			if (!blocked) break;

			// Advance to next cell
			col++;
			if (col >= cols) {
				col = 0;
				row++;
			}
		}

		const b = allBounds[i];
		let alignX = 0;
		if (opts.align.x === 'center') {
			alignX = (cellWidth - b.width) / 2;
		} else if (opts.align.x === 'end') {
			alignX = cellWidth - b.width;
		}

		let alignY = 0;
		if (opts.align.y === 'center') {
			alignY = (cellHeight - b.height) / 2;
		} else if (opts.align.y === 'end') {
			alignY = cellHeight - b.height;
		}

		arranged.push({
			id: nodes[i].id,
			x: originX + col * strideX + alignX,
			y: originY + row * strideY + alignY,
		});

		// Advance to next cell for next node
		col++;
		if (col >= cols) {
			col = 0;
			row++;
		}
	}

	// When origin is set, skip anchor (positions are absolute)
	if (opts.origin) {
		return {
			nodes: arranged.map((a) => ({ id: a.id, x: a.x, y: a.y })),
		};
	}

	const anchored = applyAnchor(nodes, arranged, opts.anchor);
	return {
		nodes: anchored.map((a) => ({ id: a.id, x: a.x, y: a.y })),
	};
}
